/**
 * translatedWordAtoms.ts — PLAT-11483 (epic PLAT-11407)
 *
 * Word-level stepping inside a COARSE translated phrase.
 *
 * Why this exists (the diagnosis, verified against Toro's real translation
 * data — see the plan doc): the on-demand splitter subdivides a sentence into
 * caption phrases keyed on SOURCE indices, and its dominant rule is that any
 * span the target reorders must stay ONE phrase. Japanese is verb-final and
 * reorders almost everything relative to English, so the rule — correctly —
 * collapses most of a Japanese sentence into a single phrase: measured on
 * `fY5zodftSV-nJWf8GKzDxA`, 79% of the Japanese text sits in phrases covering
 * more than four source words, against ~40% for ru/es/fr. Phrases ARE the
 * cursor's unit (`Transcript.tsx`'s `phraseStepIndices`), so arrow keys that
 * feel word-sized in Russian jump whole sentences in Japanese.
 *
 * The fix is display-side and additive: tile a coarse phrase's translated text
 * into words (PLAT-11480's `Intl.Segmenter` tokenizer, which is the only thing
 * that can find word boundaries in spaceless text) and map each word to one of
 * the source indices the phrase already owns. The mapping is PROPORTIONAL and
 * deliberately NOT a claim of word-to-word correspondence — it cannot be, for
 * exactly the reordering reason above. It is only used to pick which of the
 * phrase's own source words the cursor rests on, so:
 *   - every stop is a real source element with a real `ts` (the seek is honest,
 *     landing inside the phrase's own spoken window), and
 *   - nothing here is persisted, and it never reaches caption timing, the
 *     stored alignment, or edit targeting.
 *
 * Latin/Cyrillic are untouched: the tiling is gated on `containsWideChar`, so
 * every existing language keeps byte-identical stepping.
 *
 * Pure — no React, no MobX, no store imports.
 */

import { tokenizePhraseWords } from '@src/editor/services/karaokeWordTiming';
import { containsWideChar } from '@src/utility/strings';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationPhrase } from '@src/types.ts/translation';

/** One display word inside a coarse translated phrase. */
export interface TranslatedWordAtom {
  /** The word run itself, verbatim from the translated text. */
  text: string;
  /** Start offset of `text` within the phrase's translated value. */
  startOffset: number;
  /** End offset (exclusive) of `text` within the phrase's translated value. */
  endOffset: number;
  /**
   * The phrase's own source index this word rests on — proportional, NOT an
   * alignment claim (see the module doc). Non-decreasing across the atoms.
   */
  sourceIndex: number;
}

/** A word run's text and its offsets — the index-independent half of an atom. */
type WordTile = Pick<TranslatedWordAtom, 'text' | 'startOffset' | 'endOffset'>;

// Tiling is pure in the text, and the views re-run it for every phrase on
// every playhead tick — so memoize it. `Intl.Segmenter` is the expensive part
// and a transcript's phrase texts are a small, stable set. Keyed by the exact
// text, so an edit simply misses; bounded by a wholesale clear rather than an
// LRU, which is enough for a per-transcript working set.
const MAX_TILE_CACHE = 1000;
const tileCache = new Map<string, WordTile[]>();

function tileWords(text: string): WordTile[] {
  const cached = tileCache.get(text);
  if (cached) return cached;
  let tiles: WordTile[] = [];
  // Latin/Cyrillic keep today's whole-phrase rendering and stepping — the
  // splitter already fragments them, so there is nothing to subdivide.
  if (containsWideChar(text)) {
    const { pieces } = tokenizePhraseWords(text);
    const built: WordTile[] = [];
    let offset = 0;
    for (const piece of pieces) {
      const start = offset;
      offset += piece.length;
      // Whitespace runs are separators, never stops — Korean is full-width
      // AND space-delimited, so a wide phrase can legitimately carry spaces.
      if (piece.trim().length === 0) continue;
      built.push({ text: piece, startOffset: start, endOffset: offset });
    }
    tiles = built;
  }
  if (tileCache.size >= MAX_TILE_CACHE) tileCache.clear();
  tileCache.set(text, tiles);
  return tiles;
}

/**
 * Tile `translatedValue` into per-word atoms mapped onto `sourceTextIndices`
 * (the phrase's KEPT text-element indices, ascending).
 *
 * Returns `[]` — meaning "no sub-stepping, use the phrase as one unit" — when
 * the phrase is already atomic enough to gain nothing:
 *   - the text is not full-width (Latin/Cyrillic keep today's behavior),
 *   - fewer than two source indices to distribute across, or
 *   - the tokenizer finds fewer than two words.
 */
export function buildTranslatedWordAtoms(
  translatedValue: string,
  sourceIndices: readonly number[],
  isStoppableIndex?: (index: number) => boolean,
): TranslatedWordAtom[] {
  // Tile first: the tiler is cached and gates on full-width text, so a Latin
  // caller pays one regex and never the filter below.
  const tiles = tileWords(translatedValue);
  if (tiles.length < 2) return [];

  // A phrase's `originalIndices` include its punctuation and whitespace
  // elements, which carry no usable `ts` — resting a word on one makes the
  // click-to-seek a silent no-op. Callers that can tell text from punct pass
  // the predicate; `buildPhraseStepIndices` filters before calling instead.
  const sourceTextIndices = isStoppableIndex
    ? sourceIndices.filter(isStoppableIndex)
    : sourceIndices;
  if (sourceTextIndices.length < 2) return [];

  const total = sourceTextIndices.length;
  return tiles.map((tile, i) => ({
    ...tile,
    sourceIndex:
      sourceTextIndices[
        Math.min(total - 1, Math.floor((i * total) / tiles.length))
      ],
  }));
}

/**
 * The cursor stops a phrase contributes: its mapped source indices, de-duped
 * and ascending. Empty when the phrase should stay one stop.
 *
 * When a phrase has more words than source indices (a translation that fans
 * one source word out into several target words) the surplus words share an
 * index and collapse to one stop — the cursor's identity is a source index, so
 * `min(words, sourceIndices)` is the finest stepping that stays addressable.
 */
export function translatedWordStopIndices(
  translatedValue: string,
  sourceTextIndices: readonly number[],
): number[] {
  const atoms = buildTranslatedWordAtoms(translatedValue, sourceTextIndices);
  if (atoms.length === 0) return [];
  const stops: number[] = [];
  for (const atom of atoms) {
    if (stops.length === 0 || stops[stops.length - 1] !== atom.sourceIndex) {
      stops.push(atom.sourceIndex);
    }
  }
  return stops.length > 1 ? stops : [];
}

/**
 * The cursor stops for a whole translated transcript — the model behind
 * `Transcript.tsx`'s `phraseStepIndices`, extracted so the component doesn't
 * carry it (CLAUDE.md guardrail 5).
 *
 * One stop per phrase that has at least one KEPT text member (fully-cut
 * phrases are skipped so the cursor can't land on a struck phrase in
 * clip-mode), except that a full-width phrase sub-steps through its own
 * source words — see the module doc. Ascending, and de-duped across phrases
 * so two phrases resolving to the same source word can't stall an arrow press.
 */
export function buildPhraseStepIndices(
  phrases: readonly TranslationPhrase[],
  transcriptElements: readonly TranscriptElement[],
): number[] {
  const elByIndex = new Map<number, TranscriptElement>();
  for (const el of transcriptElements) elByIndex.set(el.initial_index, el);

  const indices: number[] = [];
  for (const phrase of phrases) {
    const keptText: number[] = [];
    for (const idx of phrase.originalIndices) {
      const el = elByIndex.get(idx);
      if (
        el &&
        el.type === 'text' &&
        el.state !== 'removed' &&
        el.state !== 'cut'
      ) {
        keptText.push(idx);
      }
    }
    if (keptText.length === 0) continue;
    keptText.sort((a, b) => a - b);
    const subStops = translatedWordStopIndices(
      phrase.translatedValue ?? '',
      keptText,
    );
    if (subStops.length > 0) indices.push(...subStops);
    else indices.push(keptText[0]);
  }
  indices.sort((a, b) => a - b);
  return [...new Set(indices)];
}
